import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { 
  ArrowLeft,
  Package,
  Calculator,
  UserCheck,
  Plus,
  AlertTriangle,
  Zap,
  Users,
  ShoppingBag,
  Loader2,
  DollarSign,
  TrendingUp,
  UserPlus,
  Edit,
  ArrowUp,
  ArrowDown,
  History,
  ExternalLink,
  Globe
} from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks/use-auth';
import { NewVisitModal } from '@/components/visits/NewVisitModal';
import { AddCustomerModal } from '@/components/customers/AddCustomerModal';
import { useToast } from '@/hooks/use-toast';
import { logActivity } from '@/lib/activity-log';
import { useLocation } from '@/contexts/LocationContext';
import { useProfitSettings } from '@/hooks/use-profit-settings';

/** Row from arcelik_public_products (scraped from arcelik.com.tr). RLS: authenticated can select. */
type ArcelikPublicProduct = {
  id: string;
  product_code: string | null;
  sku: string | null;
  product_name: string;
  brand: string | null;
  category: string | null;
  sub_category: string | null;
  price: number | null;
  discounted_price: number | null;
  currency: string;
  image_url: string | null;
  product_url: string | null;
  source_url: string | null;
  scrape_date: string;
  scraped_at: string;
  created_at: string;
};

type Product = Database['public']['Tables']['products']['Row'] & {
  brand?: string;
  location?: string;
  energyClass?: string;
  criticalStock: number;
  specifications: Record<string, string>;
};

type Customer = Database['public']['Tables']['customers']['Row'];
type Package = Database['public']['Tables']['packages']['Row'] & {
  customer?: {
    first_name: string | null;
    last_name: string | null;
  };
  package_items?: Array<{
    id: string;
    quantity: number;
  }>;
};

// Helper functions
const formatPrice = (price: number) => `₺${price.toLocaleString('tr-TR')}`;

const getProductMeta = (meta: any) => {
  if (!meta) return {
    brand: '',
    location: '',
    energyClass: '',
    criticalStock: 5,
    specifications: {}
  };
  return {
    brand: meta.brand || '',
    location: meta.location || '',
    energyClass: meta.energyClass || '',
    criticalStock: meta.criticalStock || 5,
    specifications: meta.specifications || {}
  };
};

const getCustomerFullName = (customer: Customer) => {
  const firstName = customer.first_name || '';
  const lastName = customer.last_name || '';
  return `${firstName} ${lastName}`.trim() || 'Adsız Müşteri';
};

type InventoryMovementRow = Database['public']['Tables']['inventory_movements']['Row'] & {
  actor?: { full_name: string | null; email: string | null } | null;
};

const MOVEMENT_FETCH_BATCH_SIZE = 1000;

/** Positive = stock increases, negative = decreases */
function signedMovementDelta(m: Pick<InventoryMovementRow, 'movement_type' | 'qty'>): number {
  const t = m.movement_type;
  const q = m.qty ?? 0;
  if (
    t === 'initial' ||
    t === 'purchase_in' ||
    t === 'adjustment_in' ||
    t === 'return_in'
  ) {
    return q;
  }
  return -q;
}

/**
 * Reconstructs per-row stock before/after from current quantity and movement list (chronological).
 * Newest-first for display.
 */
function computeMovementBalances(
  movements: InventoryMovementRow[],
  currentQty: number,
): Array<{
  movement: InventoryMovementRow;
  stockBefore: number;
  stockAfter: number;
  signedDelta: number;
}> {
  const sorted = [...movements].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const sum = sorted.reduce((s, m) => s + signedMovementDelta(m), 0);
  let running = currentQty - sum;
  const forward = sorted.map((m) => {
    const signedDelta = signedMovementDelta(m);
    const stockBefore = running;
    running += signedDelta;
    return { movement: m, stockBefore, stockAfter: running, signedDelta };
  });
  return forward.reverse();
}

const ProductDetail = () => {
  console.log('🔍 [ProductDetail] Component render started');
  const { id } = useParams();
  const navigate = useNavigate();
  const { companyId, user } = useAuth();
  const { currentLocation } = useLocation();
  const { settings: profitSettings } = useProfitSettings();
  
  console.log('🔍 [ProductDetail] Hook: useParams, useNavigate, useAuth, useLocation - Lines ~88-91');
  
  // State initialization
  const [salePrice, setSalePrice] = useState('');
  const [discount, setDiscount] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [requestedPrice, setRequestedPrice] = useState('');
  const [specialPriceQuantity, setSpecialPriceQuantity] = useState(1);
  const [specialPriceNote, setSpecialPriceNote] = useState('');
  const [visitNote, setVisitNote] = useState('');
  const [showNewVisitModal, setShowNewVisitModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [calculatedSpecialPrice, setCalculatedSpecialPrice] = useState('₺0');
  const [openPackages, setOpenPackages] = useState<Package[]>([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [packageQuantity, setPackageQuantity] = useState(1);
  const [onHoldRecords, setOnHoldRecords] = useState<any[]>([]);
  const [lastSales, setLastSales] = useState<any[]>([]);
  const [movements, setMovements] = useState<InventoryMovementRow[]>([]);
  const [movementExtras, setMovementExtras] = useState<
    Record<string, { line: string; href?: string; customerId?: string; customerName?: string }>
  >({});
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const [isSpecialPriceOpen, setIsSpecialPriceOpen] = useState(false);
  const [isPackageOpen, setIsPackageOpen] = useState(false);
  const [marsPrices, setMarsPrices] = useState<any | null>(null);
  const [arcelikProduct, setArcelikProduct] = useState<ArcelikPublicProduct | null>(null);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('');
  const [adjustmentPrice, setAdjustmentPrice] = useState('');
  const [adjustmentNote, setAdjustmentNote] = useState('');
  const [isAdjusting, setIsAdjusting] = useState(false);
  
  // Edit product form states
  const [editName, setEditName] = useState('');
  const [editItemCode, setEditItemCode] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editQuantity, setEditQuantity] = useState('');
  const [editInitialCost, setEditInitialCost] = useState('');
  const [editCurrentCost, setEditCurrentCost] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editBrand, setEditBrand] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editEnergyClass, setEditEnergyClass] = useState('');
  const [editCriticalStock, setEditCriticalStock] = useState('');
  const { toast } = useToast();

  console.log('🔍 [ProductDetail] Hook: All useState hooks completed - Lines ~94-128');
  console.log('🔍 [ProductDetail] Hook: useToast - Line ~129');

  // Data states
  const [product, setProduct] = useState<Product | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  
  // Ref to track last initialized product ID to prevent infinite loops
  const lastInitializedProductId = useRef<string | null>(null);
  const lastDialogState = useRef<boolean>(false);
  
  // Store product data in refs to avoid dependency issues - update synchronously, not in effect
  const productDataRef = useRef<{ id: string; name: string; item_code: string; category: string; quantity_on_hand: number; initial_cost: number; current_cost: number; price: number; meta: any } | null>(null);
  const lastStoredProductIdRef = useRef<string | null>(null);
  
  console.log('🔍 [ProductDetail] Hook: All useRef hooks completed - Lines ~138-143');
  
  // Stable product ID string value to prevent unnecessary useEffect triggers
  // Use useMemo to ensure it only changes when the actual ID value changes
  const productIdString = useMemo(() => {
    return product?.id || null;
  }, [product?.id]);
  
  // Stable price value to prevent unnecessary useEffect triggers
  const productPrice = useMemo(() => {
    return product?.price || 0;
  }, [product?.price]);

  console.log('🔍 [ProductDetail] Hook: useMemo hooks completed - Lines ~147-154');

  // Calculate profit from sale price, discount, and latest cost
  const calculateProfit = useMemo(() => {
    if (!product || !salePrice) return null;
    const salePriceNum = parseFloat(salePrice) || 0;
    const discountNum = parseFloat(discount) || 0;
    const latestCost = product.current_cost || 0;
    
    if (salePriceNum === 0 || latestCost === 0) return null;
    
    const priceAfterDiscount = salePriceNum - discountNum;
    const profitPerUnit = priceAfterDiscount - latestCost;
    const totalProfit = profitPerUnit * quantity;
    const profitMargin = (profitPerUnit / priceAfterDiscount) * 100;
    
    return {
      priceAfterDiscount,
      profitPerUnit,
      totalProfit,
      profitMargin,
      totalRevenue: priceAfterDiscount * quantity
    };
  }, [salePrice, discount, quantity, product?.current_cost]);

  // Calculate special price when requested price changes
  useEffect(() => {
    console.log('🔍 [ProductDetail] Hook: useEffect (calculatedSpecialPrice) - Line ~180');
    if (requestedPrice) {
      const price = parseFloat(requestedPrice) || 0;
      setCalculatedSpecialPrice(formatPrice(price));
    } else if (productPrice > 0) {
      setCalculatedSpecialPrice(formatPrice(productPrice));
    }
  }, [productPrice, requestedPrice]);

  const stockStatus = useMemo(() => {
    if (!product) {
      return { label: 'Bilinmiyor', color: 'bg-muted/20 text-muted-foreground' };
    }
    
    const productMeta = getProductMeta(product.meta);
    
    if (product.quantity_on_hand === 0) {
      return { label: 'Tükendi', color: 'bg-destructive/20 text-destructive' };
    }
    
    if (product.quantity_on_hand <= productMeta.criticalStock) {
      return { label: 'Kritik Stok', color: 'bg-warning/20 text-warning' };
    }
    
    return { label: 'Stokta', color: 'bg-success/20 text-success' };
  }, [product?.quantity_on_hand, product?.meta]);

  const onHoldSummary = useMemo(() => {
    const totalQuantity = onHoldRecords.reduce((sum, record) => sum + Number(record.hold_quantity || 0), 0);
    const totalAmount = onHoldRecords.reduce(
      (sum, record) => sum + (Number(record.hold_price || 0) * Number(record.hold_quantity || 0)),
      0,
    );
    const oldestDays = onHoldRecords.reduce((maxDays, record) => {
      const days = Math.floor((Date.now() - new Date(record.date_on_hold).getTime()) / (1000 * 60 * 60 * 24));
      return Math.max(maxDays, days);
    }, 0);

    return {
      count: onHoldRecords.length,
      totalQuantity,
      totalAmount,
      oldestDays,
    };
  }, [onHoldRecords]);

  // Check if cost date is from a different month than current month
  const costDateWarning = useMemo(() => {
    if (!product?.last_price_update) return null;
    
    const costDate = new Date(product.last_price_update);
    const now = new Date();
    
    const costMonth = costDate.getMonth();
    const costYear = costDate.getFullYear();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Check if cost date is from a different month/year
    if (costMonth !== currentMonth || costYear !== currentYear) {
      const monthNames = [
        'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
        'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
      ];
      
      return {
        show: true,
        costMonth: monthNames[costMonth],
        costYear: costYear,
        currentMonth: monthNames[currentMonth],
        currentYear: currentYear
      };
    }
    
    return null;
  }, [product?.last_price_update]);

  const movementsWithBalance = useMemo(() => {
    if (!product || !movements.length) return [];
    return computeMovementBalances(movements, product.quantity_on_hand ?? 0);
  }, [movements, product?.quantity_on_hand, product?.id]);

  const displayedMovementsWithBalance = useMemo(
    () => movementsWithBalance,
    [movementsWithBalance],
  );

  const fetchAllInventoryMovements = useCallback(
    async (productId: string, companyIdValue: string) => {
      const allRows: InventoryMovementRow[] = [];
      let from = 0;

      while (true) {
        const to = from + MOVEMENT_FETCH_BATCH_SIZE - 1;
        const { data, error } = await supabase
          .from('inventory_movements')
          .select(`
            *,
            actor:user_profiles(full_name, email)
          `)
          .eq('product_id', productId)
          .eq('company_id', companyIdValue)
          .order('created_at', { ascending: false })
          .range(from, to);

        if (error) throw error;

        const batch = (data || []) as InventoryMovementRow[];
        allRows.push(...batch);

        if (batch.length < MOVEMENT_FETCH_BATCH_SIZE) {
          break;
        }

        from += MOVEMENT_FETCH_BATCH_SIZE;
      }

      return allRows;
    },
    [],
  );

  const getPackageDisplayName = useCallback((pkg: Package) => {
    const customerName = pkg.customer ? 
      `${pkg.customer.first_name || ''} ${pkg.customer.last_name || ''}`.trim() :
      'Müşterisiz';
    
    const itemCount = pkg.package_items?.length || 0;
    
    return `${pkg.title || `Paket ${pkg.id.slice(0, 8)}`} - ${customerName} (${itemCount} ürün)`;
  }, []);

  console.log('🔍 [ProductDetail] Hook: useMemo (stockStatus) and useCallback completed - Lines ~189-207');

  useEffect(() => {
    if (!movements.length || !companyId) {
      setMovementExtras({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const eItemIds = [
        ...new Set(
          movements
            .filter((m) => m.related_table === 'eirsaliye_item_import' && m.related_id)
            .map((m) => m.related_id as string),
        ),
      ];
      const tradeIds = [
        ...new Set(
          movements
            .filter((m) => m.related_table === 'trades' && m.related_id)
            .map((m) => m.related_id as string),
        ),
      ];
      const salesIds = [
        ...new Set(
          movements
            .filter((m) => m.related_table === 'sales' && m.related_id)
            .map((m) => m.related_id as string),
        ),
      ];

      const customerBySaleId = new Map<string, { id: string; name: string }>();
      if (salesIds.length) {
        const { data: salesRows } = await supabase
          .from('sales')
          .select('id, customer:customers(id, first_name, last_name)')
          .in('id', salesIds);
        for (const s of salesRows || []) {
          const c = (s as any).customer;
          if (c?.id) {
            const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Adsız Müşteri';
            customerBySaleId.set(s.id as string, { id: c.id, name });
          }
        }
      }

      const irsaliyeByItemId = new Map<string, string>();
      if (eItemIds.length) {
        const { data: itemRows } = await supabase
          .from('eirsaliye_items')
          .select('id, eirsaliye_id')
          .in('id', eItemIds);
        const eids = [
          ...new Set(
            (itemRows || []).map((r) => r.eirsaliye_id).filter(Boolean),
          ),
        ] as string[];
        if (eids.length) {
          const { data: irs } = await supabase
            .from('eirsaliyes')
            .select('id, irsaliye_no')
            .in('id', eids);
          const noByEid = new Map((irs || []).map((i) => [i.id, i.irsaliye_no as string]));
          for (const row of itemRows || []) {
            if (row.id && row.eirsaliye_id) {
              const no = noByEid.get(row.eirsaliye_id);
              if (no) irsaliyeByItemId.set(row.id, no);
            }
          }
        }
      }

      const tradeLabelById = new Map<string, string>();
      if (tradeIds.length) {
        const { data: trows } = await supabase
          .from('trades')
          .select('id, trade_date, trade_type')
          .in('id', tradeIds);
        for (const t of trows || []) {
          const d = t.trade_date
            ? new Date(t.trade_date).toLocaleDateString('tr-TR')
            : '';
          tradeLabelById.set(
            t.id,
            d ? `${d} · ${t.trade_type || 'ticaret'}` : (t.trade_type || 'İşlem'),
          );
        }
      }

      const next: Record<string, { line: string; href?: string; customerId?: string; customerName?: string }> = {};
      for (const m of movements) {
        const rt = m.related_table;
        const rid = m.related_id;
        if (rt === 'sales' && rid) {
          const cust = customerBySaleId.get(rid as string);
          next[m.id] = {
            line: 'Satış kaydı ile stoktan düşüldü.',
            href: `/sales/${rid}`,
            customerId: cust?.id,
            customerName: cust?.name,
          };
        } else if (rt === 'eirsaliye_item_import' && rid) {
          const no = irsaliyeByItemId.get(rid as string);
          next[m.id] = {
            line: no
              ? `e-İrsaliye üzerinden stok girişi (İrsaliye no: ${no}).`
              : 'e-İrsaliye satırından stok girişi.',
          };
        } else if (rt === 'trades' && rid) {
          const label = tradeLabelById.get(rid as string);
          next[m.id] = {
            line: label ? `Bayi ticareti (${label}).` : 'Bayi ticareti / virman.',
          };
        } else if (rt === 'manual_entry') {
          next[m.id] = { line: 'Stok girişi (Excel / toplu giriş) ile miktar artırıldı.' };
        } else if (rt === 'manual_adjustment') {
          next[m.id] = { line: 'Ürün düzenleme ekranından stok miktarı güncellendi.' };
        }
      }
      if (!cancelled) setMovementExtras(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [movements, companyId]);

  // Fetch available categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!companyId) return;
      
      try {
        const { data, error } = await supabase
          .from('products')
          .select('category')
          .eq('company_id', companyId)
          .not('category', 'is', null);
        
        if (error) {
          console.error('Error fetching categories:', error);
          return;
        }
        
        // Get unique categories, filter out empty strings, and sort
        const uniqueCategories = Array.from(new Set(
          (data || [])
            .map(p => p.category)
            .filter((cat): cat is string => Boolean(cat) && cat.trim() !== '')
        )).sort();
        
        setAvailableCategories(uniqueCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    
    void fetchCategories();
  }, [companyId]);

  // Load product, customers and packages data
  useEffect(() => {
    console.log('🔍 [ProductDetail] Hook: useEffect (fetchData) - Line ~218');
    let isMounted = true; // Flag to prevent state updates after unmount
    
    const fetchData = async () => {
      if (!id || !companyId) return;
      
      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        
        // Fetch product
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .eq('company_id', companyId)
          .single();
          
        if (productError) throw productError;
        
        if (productData && isMounted) {
          const productMeta = getProductMeta(productData.meta);
          const transformedProduct: Product = {
            ...productData,
            ...productMeta,
            specifications: productMeta.specifications
          };
          
          setProduct(transformedProduct);
          setCalculatedSpecialPrice(formatPrice(productData.price));
          
          // Reset Mars and Arçelik data before fetching new ones
          setMarsPrices(null);
          setArcelikProduct(null);
        }
        
        // Fetch on hold records for this product
        if (isMounted && id) {
          const { data: onHoldData, error: onHoldError } = await supabase
            .from('on_holds')
            .select(`
              *,
              customer:customers(id, first_name, last_name, phone),
              product:products(name, item_code)
            `)
            .eq('product_id', id)
            .eq('company_id', companyId)
            .eq('status', 'on_hold')
            .order('date_on_hold', { ascending: false })
            .limit(10);
            
          if (!onHoldError && onHoldData) {
            setOnHoldRecords(onHoldData);
          }
        }
        
        // Fetch last sales for this product
        if (isMounted && id) {
          const { data: salesData, error: salesError } = await supabase
            .from('sale_items')
            .select(`
              *,
              sale:sales(
                id,
                sale_datetime,
                total_net,
                customer:customers(id, first_name, last_name)
              )
            `)
            .eq('product_id', id)
            .eq('company_id', companyId)
            .order('created_at', { ascending: false })
            .limit(10);
            
          if (!salesError && salesData) {
            setLastSales(salesData);
          }
        }
        
        // Fetch inventory movements for this product
        if (isMounted && id) {
          const movementsData = await fetchAllInventoryMovements(id, companyId);
          if (isMounted) {
            setMovements(movementsData);
          }
        }
        
        // Fetch Mars product prices for this product (using item_code as SKU)
        if (isMounted && productData?.item_code) {
          const { data: marsPricesData, error: marsPricesError } = await supabase
            .from('mars_product_prices')
            .select('*')
            .eq('sku', productData.item_code)
            .order('last_updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();
            
          if (!marsPricesError && marsPricesData) {
            if (isMounted) {
              setMarsPrices(marsPricesData);
            }
          } else if (marsPricesError && marsPricesError.code !== 'PGRST116') {
            // PGRST116 is "no rows returned" - not an error, just no data
            console.error('Error fetching Mars prices:', marsPricesError);
            if (isMounted) {
              setMarsPrices(null);
            }
          } else if (isMounted) {
            // No data found
            setMarsPrices(null);
          }
        }

        // Fetch Arçelik public store data (arcelik_public_products): match by sku or product_code
        if (isMounted && productData?.item_code) {
          const itemCode = productData.item_code.trim();
          let arcelikData: ArcelikPublicProduct | null = null;
          const { data: bySku } = await supabase
            .from('arcelik_public_products')
            .select('*')
            .eq('sku', itemCode)
            .order('scrape_date', { ascending: false })
            .order('scraped_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (bySku) {
            arcelikData = bySku as ArcelikPublicProduct;
          } else {
            const { data: byCode } = await supabase
              .from('arcelik_public_products')
              .select('*')
              .eq('product_code', itemCode)
              .order('scrape_date', { ascending: false })
              .order('scraped_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            if (byCode) arcelikData = byCode as ArcelikPublicProduct;
          }
          if (isMounted) setArcelikProduct(arcelikData);
        }
        
        // Fetch customers
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('*')
          .eq('company_id', companyId)
          .order('first_name');
          
        if (customersError) throw customersError;
        
        if (isMounted) {
          setCustomers(customersData || []);
        }
        
        // Fetch open packages
        const { data: packagesData, error: packagesError } = await supabase
          .from('packages')
          .select(`
            *,
            customer:customers(
              first_name,
              last_name
            ),
            package_items(
              id,
              quantity
            )
          `)
          .eq('company_id', companyId)
          .in('status', ['draft', 'offered'])
          .order('created_at', { ascending: false });
          
        if (packagesError) throw packagesError;
        
        if (isMounted) {
          setOpenPackages(packagesData || []);
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        if (isMounted) {
          setError('Veri yüklenirken bir hata oluştu');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    if (id && companyId) {
      void fetchData();
    }
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [id, companyId, fetchAllInventoryMovements]);

  // Initialize edit form when dialog opens - MUST be before early returns
  // Update ref synchronously when product ID changes (not in useEffect to avoid loops)
  if (product?.id && product.id !== lastStoredProductIdRef.current) {
    productDataRef.current = {
      id: product.id,
      name: product.name,
      item_code: product.item_code,
      category: product.category,
      quantity_on_hand: product.quantity_on_hand,
      initial_cost: product.initial_cost || 0,
      current_cost: product.current_cost || 0,
      price: product.price,
      meta: product.meta,
    };
    lastStoredProductIdRef.current = product.id;
    console.log('🔍 [ProductDetail] Product data ref updated synchronously:', product.id);
  } else if (!product) {
    productDataRef.current = null;
    lastStoredProductIdRef.current = null;
  }

  // Effect that only runs when dialog opens/closes - MUST be before early returns
  useEffect(() => {
    console.log('🔍 [ProductDetail] Hook: useEffect (dialog) - Line ~589');
    const productData = productDataRef.current;
    const currentProductId = productData?.id || null;
    
    console.log('🔍 [ProductDetail useEffect] Dialog state changed:', {
      showAdjustmentDialog,
      lastDialogState: lastDialogState.current,
      currentProductId,
      lastInitializedProductId: lastInitializedProductId.current,
      hasProductData: !!productData,
    });
    
    // Dialog just closed - reset tracking
    if (!showAdjustmentDialog && lastDialogState.current) {
      console.log('🔍 [ProductDetail] Dialog closed, resetting');
      lastInitializedProductId.current = null;
      lastDialogState.current = false;
      return;
    }
    
    // Dialog just opened - initialize form
    if (showAdjustmentDialog && !lastDialogState.current) {
      console.log('🔍 [ProductDetail] Dialog opened, checking if initialization needed');
      lastDialogState.current = true;
      
      // Only initialize if we have product data and haven't initialized for this product ID
      if (productData && currentProductId && currentProductId !== lastInitializedProductId.current) {
        console.log('🔍 [ProductDetail] Initializing form for product:', currentProductId);
        
        // Batch all state updates together
        setEditName(productData.name);
        setEditItemCode(productData.item_code);
        setEditCategory(productData.category);
        setEditQuantity(productData.quantity_on_hand.toString());
        setEditInitialCost(productData.initial_cost.toString());
        setEditCurrentCost(productData.current_cost.toString());
        setEditPrice(productData.price.toString());
        const meta = getProductMeta(productData.meta);
        setEditBrand(meta.brand);
        setEditLocation(meta.location);
        setEditEnergyClass(meta.energyClass);
        setEditCriticalStock(meta.criticalStock.toString());
        
        // Mark as initialized AFTER state updates are queued
        lastInitializedProductId.current = currentProductId;
        
        console.log('🔍 [ProductDetail] Form initialized, ref updated:', {
          lastInitializedProductId: lastInitializedProductId.current,
        });
      } else {
        console.log('🔍 [ProductDetail] Skipping initialization', {
          hasProductData: !!productData,
          currentProductId,
          lastInitializedProductId: lastInitializedProductId.current,
          alreadyInitialized: currentProductId === lastInitializedProductId.current,
        });
      }
    }
  }, [showAdjustmentDialog]); // Only depend on showAdjustmentDialog to prevent loops

  console.log('🔍 [ProductDetail] All hooks completed. About to check early returns. Current state:', {
    loading,
    error,
    hasProduct: !!product,
    productId: product?.id
  });

  if (loading) {
    console.log('🔍 [ProductDetail] Early return: loading=true');
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <Loader2 className="w-16 h-16 animate-spin text-muted-foreground" />
          <h2 className="text-xl font-semibold">Ürün Yükleniyor</h2>
          <p className="text-muted-foreground">Lütfen bekleyin...</p>
        </div>
      </Layout>
    );
  }

  if (error || !product) {
    console.log('🔍 [ProductDetail] Early return: error or no product', { error, hasProduct: !!product });
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <AlertTriangle className="w-16 h-16 text-destructive" />
          <h2 className="text-xl font-semibold">{error || 'Ürün bulunamadı'}</h2>
          <Button onClick={() => navigate('/products')}>
            Ürün Listesine Dön
          </Button>
        </div>
      </Layout>
    );
  }


  const handleSpecialPriceRequest = () => {
    if (!selectedCustomer || !requestedPrice) return;
    
    console.log('Special price request:', {
      customerId: selectedCustomer,
      requestedPrice: calculatedSpecialPrice,
      quantity: specialPriceQuantity,
      note: specialPriceNote
    });
    
    toast({
      title: "Özel Fiyat Talebi Gönderildi",
      description: `${specialPriceQuantity} adet için ${calculatedSpecialPrice} fiyat talebi oluşturuldu`,
    });
    
    // Reset form
    setSelectedCustomer('');
    setRequestedPrice('');
    setSpecialPriceQuantity(1);
    setSpecialPriceNote('');
  };

  const handleAddToVisit = () => {
    if (!selectedCustomer) return;
    
    console.log('Add to visit:', {
      productId: product.id,
      customerId: selectedCustomer,
      note: visitNote
    });
  };

  const handleAddToPackage = async () => {
    if (!product || !companyId) return;
    
    try {
      if (selectedPackage && selectedPackage !== 'create-new') {
        // Add to existing package
        const { error } = await supabase
          .from('package_items')
          .insert({
            package_id: selectedPackage,
            product_id: product.id,
            quantity: packageQuantity,
            quoted_price_each: product.price,
            line_total_gross: product.price * packageQuantity,
            line_total_net: product.price * packageQuantity,
            discount: 0,
            company_id: companyId
          });
          
        if (error) throw error;
        
        toast({
          title: "Ürün Pakete Eklendi",
          description: `${product.name} başarıyla pakete eklendi`,
        });
      } else {
        // Create new package
        const { data: newPackage, error: packageError } = await supabase
          .from('packages')
          .insert({
            company_id: companyId,
            title: `${product.name} Paketi`,
            status: 'draft',
            total_gross: product.price * packageQuantity,
            total_net: product.price * packageQuantity,
            total_discount: 0
          })
          .select()
          .single();
          
        if (packageError) throw packageError;
        
        // Add item to new package
        const { error: itemError } = await supabase
          .from('package_items')
          .insert({
            package_id: newPackage.id,
            product_id: product.id,
            quantity: packageQuantity,
            quoted_price_each: product.price,
            line_total_gross: product.price * packageQuantity,
            line_total_net: product.price * packageQuantity,
            discount: 0,
            company_id: companyId
          });
          
        if (itemError) throw itemError;
        
        toast({
          title: "Yeni Paket Oluşturuldu",
          description: `${product.name} ile yeni paket oluşturuldu`,
        });
        
        // Refresh packages
        const { data: updatedPackages } = await supabase
          .from('packages')
          .select(`
            *,
            customer:customers(
              first_name,
              last_name
            ),
            package_items(
              id,
              quantity
            )
          `)
          .eq('company_id', companyId)
          .in('status', ['draft', 'offered'])
          .order('created_at', { ascending: false });
          
        setOpenPackages(updatedPackages || []);
      }
      
      // Reset form
      setSelectedPackage('');
      setPackageQuantity(1);
      
    } catch (error) {
      console.error('Error adding to package:', error);
      toast({
        title: "Hata",
        description: "Ürün pakete eklenirken hata oluştu",
        variant: "destructive",
      });
    }
  };
  
  const handleCustomerAdded = async () => {
    // Refresh customers list
    try {
      const { data: customersData, error } = await supabase
        .from('customers')
        .select('*')
        .eq('company_id', companyId)
        .order('first_name');
        
      if (error) throw error;
      setCustomers(customersData || []);
    } catch (error) {
      console.error('Error refreshing customers:', error);
    }
  };

  const getMovementTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'initial': 'İlk Stok',
      'purchase_in': 'Alış Girişi',
      'adjustment_in': 'Düzeltme Girişi',
      'adjustment_out': 'Düzeltme Çıkışı',
      'sale_out': 'Satış Çıkışı',
      'return_in': 'İade Girişi'
    };
    return labels[type] || type;
  };

  const getMovementTypeColor = (type: string) => {
    if (type === 'sale_out' || type === 'adjustment_out') {
      return 'text-red-600 bg-red-50';
    }
    if (type === 'purchase_in' || type === 'adjustment_in' || type === 'return_in' || type === 'initial') {
      return 'text-green-600 bg-green-50';
    }
    return 'text-muted-foreground bg-muted';
  };

  const handleManualAdjustment = async () => {
    if (!product || !companyId || !user) return;
    
    setIsAdjusting(true);
    
    try {
      const updateData: any = {};
      const metaUpdate: any = {};
      let hasChanges = false;
      
      // Update name
      if (editName && editName !== product.name) {
        updateData.name = editName;
        hasChanges = true;
      }
      
      // Update item code
      if (editItemCode && editItemCode !== product.item_code) {
        updateData.item_code = editItemCode;
        hasChanges = true;
      }
      
      // Update category
      if (editCategory && editCategory !== product.category) {
        updateData.category = editCategory;
        hasChanges = true;
      }
      
      // Update quantity (direct set, not adjustment)
      const newQuantity = parseFloat(editQuantity);
      if (!isNaN(newQuantity) && newQuantity !== product.quantity_on_hand) {
        if (newQuantity < 0) {
          toast({
            title: "Hata",
            description: "Stok miktarı negatif olamaz",
            variant: "destructive",
          });
          setIsAdjusting(false);
          return;
        }
        
        const qtyChange = newQuantity - product.quantity_on_hand;
        updateData.quantity_on_hand = newQuantity;
        hasChanges = true;
        
        // Create inventory movement if quantity changed
        if (qtyChange !== 0) {
          const movementType = qtyChange > 0 ? 'adjustment_in' : 'adjustment_out';
          const { error: movementError } = await supabase
            .from('inventory_movements')
            .insert({
              company_id: companyId,
              product_id: product.id,
              movement_type: movementType,
              qty: Math.abs(qtyChange),
              related_table: 'manual_adjustment',
              related_id: null,
              actor_user_id: user.id,
            });
            
          if (movementError) throw movementError;
        }
      }
      
      // Update initial cost
      const newInitialCost = parseFloat(editInitialCost);
      if (!isNaN(newInitialCost) && newInitialCost !== (product.initial_cost || 0)) {
        updateData.initial_cost = newInitialCost;
        hasChanges = true;
      }
      
      // Update current cost
      const newCurrentCost = parseFloat(editCurrentCost);
      if (!isNaN(newCurrentCost) && newCurrentCost !== (product.current_cost || 0)) {
        updateData.current_cost = newCurrentCost;
        if (newCurrentCost > (product.current_cost || 0)) {
          updateData.last_price_update = new Date().toISOString();
        }
        hasChanges = true;
      }
      
      // Update price
      const newPrice = parseFloat(editPrice);
      if (!isNaN(newPrice) && newPrice !== product.price) {
        updateData.price = newPrice;
        hasChanges = true;
      }
      
      // Update meta fields
      const currentMeta = getProductMeta(product.meta);
      if (editBrand !== currentMeta.brand) {
        metaUpdate.brand = editBrand;
        hasChanges = true;
      }
      if (editLocation !== currentMeta.location) {
        metaUpdate.location = editLocation;
        hasChanges = true;
      }
      if (editEnergyClass !== currentMeta.energyClass) {
        metaUpdate.energyClass = editEnergyClass;
        hasChanges = true;
      }
      const newCriticalStock = parseFloat(editCriticalStock);
      if (!isNaN(newCriticalStock) && newCriticalStock !== currentMeta.criticalStock) {
        metaUpdate.criticalStock = newCriticalStock;
        hasChanges = true;
      }
      
      // Merge meta updates
      if (Object.keys(metaUpdate).length > 0) {
        updateData.meta = {
          ...product.meta,
          ...metaUpdate,
          specifications: currentMeta.specifications
        };
      }
      
      if (!hasChanges) {
        toast({
          title: "Değişiklik Yok",
          description: "Hiçbir değişiklik yapmadınız",
          variant: "destructive",
        });
        setIsAdjusting(false);
        return;
      }
      
      // Update product
      if (Object.keys(updateData).length > 0) {
        const { error: updateError } = await supabase
          .from('products')
          .update(updateData)
          .eq('id', product.id);
        
        if (updateError) throw updateError;

        // Log activity
        if (companyId) {
          await logActivity({
            action: 'update',
            entity: 'product',
            entityId: product.id,
            detail: {
              name: product.name,
              changes: updateData,
            },
            locationId: currentLocation?.id || null,
            companyId,
            userId: user?.id || null,
          });
        }
      }
      
      // Refresh product data
      const { data: updatedProduct, error: fetchError } = await supabase
        .from('products')
        .select('*')
        .eq('id', product.id)
        .eq('company_id', companyId)
        .single();
        
      if (fetchError) throw fetchError;
        
        if (updatedProduct) {
          const productMeta = getProductMeta(updatedProduct.meta);
          const transformedProduct: Product = {
            ...updatedProduct,
            ...productMeta,
            specifications: productMeta.specifications
          };
          setProduct(transformedProduct);
          
          // Reset edit form
          setEditName('');
          setEditItemCode('');
          setEditCategory('');
          setEditQuantity('');
          setEditInitialCost('');
          setEditCurrentCost('');
          setEditPrice('');
          setEditBrand('');
          setEditLocation('');
          setEditEnergyClass('');
          setEditCriticalStock('');
        }
      
      // Refresh movements
      const movementsData = await fetchAllInventoryMovements(product.id, companyId);
      setMovements(movementsData);
      
      toast({
        title: "Başarılı",
        description: "Ürün bilgileri güncellendi",
      });
      
      // Reset form
      setAdjustmentQuantity('');
      setAdjustmentPrice('');
      setAdjustmentNote('');
      setShowAdjustmentDialog(false);
      
    } catch (error: any) {
      console.error('Error adjusting product:', error);
      toast({
        title: "Hata",
        description: error.message || "Ürün güncellenirken hata oluştu",
        variant: "destructive",
      });
    } finally {
      setIsAdjusting(false);
    }
  };
  

  return (
    <Layout>
      <div className="space-y-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/products')}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Ürün Listesi
        </Button>

        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-24 h-24 bg-muted rounded-lg flex items-center justify-center">
                <Package className="w-12 h-12 text-muted-foreground" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-start gap-2">
                    <h1 className="text-2xl font-bold">{product.name}</h1>
                    {product.energyClass && (
                      <Badge variant="secondary" className="bg-green-500/20 text-green-600">
                        <Zap className="w-3 h-3 mr-1" />
                        {product.energyClass}
                      </Badge>
                    )}
                  </div>
                  <Dialog open={showAdjustmentDialog} onOpenChange={setShowAdjustmentDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2">
                        <Edit className="w-4 h-4" />
                        Düzenle
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[700px]">
                      <DialogHeader>
                        <DialogTitle>Ürün Bilgilerini Düzenle</DialogTitle>
                        <DialogDescription>
                          Ürünün tüm detaylarını düzenleyebilirsiniz.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        {/* Basic Information */}
                        <div className="space-y-3">
                          <h3 className="font-semibold text-sm text-muted-foreground uppercase">Temel Bilgiler</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="edit-name">Ürün Adı</Label>
                              <Input
                                id="edit-name"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder="Ürün adı"
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-item-code">Ürün Kodu</Label>
                              <Input
                                id="edit-item-code"
                                value={editItemCode}
                                onChange={(e) => setEditItemCode(e.target.value)}
                                placeholder="Ürün kodu"
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-category">Kategori</Label>
                              <Select value={editCategory} onValueChange={setEditCategory}>
                                <SelectTrigger id="edit-category">
                                  <SelectValue placeholder="Kategori seçin" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableCategories.map((category) => (
                                    <SelectItem key={category} value={category}>
                                      {category}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label htmlFor="edit-quantity">Stok Miktarı</Label>
                              <Input
                                id="edit-quantity"
                                type="number"
                                min="0"
                                value={editQuantity}
                                onChange={(e) => setEditQuantity(e.target.value)}
                                placeholder="Stok miktarı"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <Separator />
                        
                        {/* Cost and Price Information */}
                        <div className="space-y-3">
                          <h3 className="font-semibold text-sm text-muted-foreground uppercase">Maliyet ve Fiyat</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label htmlFor="edit-initial-cost">İlk Maliyet (₺)</Label>
                              <Input
                                id="edit-initial-cost"
                                type="number"
                                min="0"
                                step="0.01"
                                value={editInitialCost}
                                onChange={(e) => setEditInitialCost(e.target.value)}
                                placeholder="İlk maliyet"
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-current-cost">Güncel Maliyet (₺)</Label>
                              <Input
                                id="edit-current-cost"
                                type="number"
                                min="0"
                                step="0.01"
                                value={editCurrentCost}
                                onChange={(e) => setEditCurrentCost(e.target.value)}
                                placeholder="Güncel maliyet"
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-price">Satış Fiyatı (₺)</Label>
                              <Input
                                id="edit-price"
                                type="number"
                                min="0"
                                step="0.01"
                                value={editPrice}
                                onChange={(e) => setEditPrice(e.target.value)}
                                placeholder="Satış fiyatı"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <Separator />
                        
                        {/* Meta Information */}
                        <div className="space-y-3">
                          <h3 className="font-semibold text-sm text-muted-foreground uppercase">Ek Bilgiler</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="edit-brand">Marka</Label>
                              <Input
                                id="edit-brand"
                                value={editBrand}
                                onChange={(e) => setEditBrand(e.target.value)}
                                placeholder="Marka"
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-location">Konum</Label>
                              <Input
                                id="edit-location"
                                value={editLocation}
                                onChange={(e) => setEditLocation(e.target.value)}
                                placeholder="Konum"
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-energy-class">Enerji Sınıfı</Label>
                              <Input
                                id="edit-energy-class"
                                value={editEnergyClass}
                                onChange={(e) => setEditEnergyClass(e.target.value)}
                                placeholder="Enerji sınıfı"
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-critical-stock">Kritik Stok Seviyesi</Label>
                              <Input
                                id="edit-critical-stock"
                                type="number"
                                min="0"
                                value={editCriticalStock}
                                onChange={(e) => setEditCriticalStock(e.target.value)}
                                placeholder="Kritik stok"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <Separator />
                        
                        <div>
                          <Label htmlFor="adjustment-note">Not (Opsiyonel)</Label>
                          <Textarea
                            id="adjustment-note"
                            value={adjustmentNote}
                            onChange={(e) => setAdjustmentNote(e.target.value)}
                            placeholder="Değişiklik nedeni..."
                            rows={3}
                          />
                        </div>
                        
                        <Button 
                          onClick={handleManualAdjustment}
                          disabled={isAdjusting}
                          className="w-full gap-2"
                        >
                          {isAdjusting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Güncelleniyor...
                            </>
                          ) : (
                            <>
                              <Edit className="w-4 h-4" />
                              Değişiklikleri Kaydet
                            </>
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                  <span>Kod: <span className="font-mono">{product.item_code}</span></span>
                  {product.brand && <span>Marka: {product.brand}</span>}
                  <span>Kategori: {product.category}</span>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <Badge variant="secondary" className={stockStatus.color}>
                    {stockStatus.label}
                  </Badge>
                  <span className="font-semibold">
                    {product.quantity_on_hand} adet stokta
                  </span>
                  {product.quantity_on_hand <= product.criticalStock && (
                    <AlertTriangle className="w-5 h-5 text-warning" />
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  <div className="rounded-lg border-2 border-blue-200 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/20 p-3">
                    <div className="text-xs uppercase tracking-wide text-blue-700 dark:text-blue-300">Güncel Maliyet</div>
                    <div className="text-xl font-bold mt-1">{formatPrice(product.current_cost || 0)}</div>
                    <div className="text-xs text-muted-foreground mt-1">İlk: {formatPrice(product.initial_cost || 0)}</div>
                  </div>

                  <div className="rounded-lg border-2 border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20 p-3">
                    <div className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Stok Durumu</div>
                    <div className="text-xl font-bold mt-1">{product.quantity_on_hand} adet</div>
                    <div className="text-xs text-muted-foreground mt-1">Kritik seviye: {product.criticalStock} adet</div>
                  </div>

                  <div className="rounded-lg border-2 border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 p-3">
                    <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300">Beklemede</div>
                    <div className="text-xl font-bold mt-1">{onHoldSummary.totalQuantity} adet</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {onHoldSummary.count} kayıt · {formatPrice(onHoldSummary.totalAmount)}
                      {onHoldSummary.count > 0 ? ` · En eski ${onHoldSummary.oldestDays} gün` : ''}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Satış Fiyatı:</span>
                    <span className="font-semibold">{formatPrice(product.price)}</span>
                  </div>
                  {product.price > 0 && product.current_cost > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Kar Marjı:</span>
                      <span className="font-semibold text-green-600">
                        %{(((product.price - product.current_cost) / product.price) * 100).toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className={`grid grid-cols-1 gap-6 ${arcelikProduct ? 'xl:grid-cols-2' : ''}`}>
          {/* Arçelik Web (public store) — visible to all authenticated users via RLS */}
          {arcelikProduct && (
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Arçelik Web Fiyatı
                </CardTitle>
                <CardDescription>
                  arcelik.com.tr mağaza fiyatı (son çekim: {new Date(arcelikProduct.scraped_at).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })})
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-6">
                  {arcelikProduct.image_url && (
                    <div className="flex-shrink-0">
                      <img
                        src={arcelikProduct.image_url}
                        alt={arcelikProduct.product_name}
                        className="w-32 h-32 object-contain rounded-lg border bg-muted/30"
                      />
                    </div>
                  )}
                  <div className="flex-1 space-y-3 min-w-0">
                    <div>
                      <div className="text-sm text-muted-foreground">Ürün adı</div>
                      <div className="font-semibold">{arcelikProduct.product_name}</div>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm">
                      {arcelikProduct.brand && (
                        <span><span className="text-muted-foreground">Marka:</span> <span className="font-medium">{arcelikProduct.brand}</span></span>
                      )}
                      {arcelikProduct.sku && (
                        <span><span className="text-muted-foreground">SKU:</span> <span className="font-mono">{arcelikProduct.sku}</span></span>
                      )}
                      {arcelikProduct.product_code && (
                        <span><span className="text-muted-foreground">Ürün kodu:</span> <span className="font-mono">{arcelikProduct.product_code}</span></span>
                      )}
                      {arcelikProduct.category && (
                        <span><span className="text-muted-foreground">Kategori:</span> {arcelikProduct.category}</span>
                      )}
                      {arcelikProduct.sub_category && (
                        <span><span className="text-muted-foreground">Alt kategori:</span> {arcelikProduct.sub_category}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-baseline gap-4 pt-2">
                      {arcelikProduct.price != null && (
                        <div>
                          <span className="text-sm text-muted-foreground">Liste fiyatı: </span>
                          <span className={arcelikProduct.discounted_price != null ? 'text-muted-foreground line-through' : 'font-semibold'}>
                            {formatPrice(Number(arcelikProduct.price))}
                          </span>
                          {arcelikProduct.currency && arcelikProduct.currency !== 'TRY' && (
                            <span className="text-muted-foreground ml-1">{arcelikProduct.currency}</span>
                          )}
                        </div>
                      )}
                      {arcelikProduct.discounted_price != null && (
                        <div>
                          <span className="text-sm text-muted-foreground">İndirimli fiyat: </span>
                          <span className="font-semibold text-accent">
                            {formatPrice(Number(arcelikProduct.discounted_price))}
                          </span>
                        </div>
                      )}
                    </div>
                    {arcelikProduct.product_url && (
                      <a
                        href={arcelikProduct.product_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Arçelik sayfasında görüntüle
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cost Information */}
          <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Maliyet Bilgisi
            </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {costDateWarning && (
                <div className="p-3 rounded-lg bg-warning/10 border border-warning/20 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold text-warning mb-1">Maliyet Tarihi Uyarısı</div>
                    <div className="text-sm text-muted-foreground">
                      Bu ürünün maliyeti <strong>{costDateWarning.costMonth} {costDateWarning.costYear}</strong> ayında girilmiş. 
                      Şu anda <strong>{costDateWarning.currentMonth} {costDateWarning.currentYear}</strong> ayındayız. 
                      Maliyet bilgisinin güncel olup olmadığını kontrol edin.
                    </div>
                  </div>
                </div>
              )}
              <div className="rounded-lg border divide-y">
                <div className="flex justify-between items-center px-3 py-1.5">
                  <span className="text-sm text-muted-foreground">İlk Maliyet</span>
                  <span className="text-sm font-semibold">{formatPrice(product.initial_cost || 0)}</span>
                </div>
                <div className="flex justify-between items-center px-3 py-1.5">
                  <span className="text-sm text-muted-foreground">Güncel Maliyet</span>
                  <span className="text-sm font-semibold">{formatPrice(product.current_cost || 0)}</span>
                </div>
                <div className="flex justify-between items-center px-3 py-1.5">
                  <span className="text-sm text-muted-foreground">Satış Fiyatı</span>
                  <span className="text-sm font-semibold">{formatPrice(product.price)}</span>
                </div>
              </div>
              
              {/* Mars Product Prices */}
              {marsPrices && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-muted-foreground">Güncel Maliyet (Mars)</h3>
                      {marsPrices.last_updated_at && (
                        <span className="text-xs text-muted-foreground">
                          Son Güncelleme: {new Date(marsPrices.last_updated_at).toLocaleString('tr-TR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </div>
                    <div className="rounded-lg border divide-y">
                      {[
                        { key: 'pesin', label: 'Peşin Fiyat', value: marsPrices.pesin_price || '0' },
                        { key: '30', label: '30 Gün', value: marsPrices.price_30 || '0' },
                        { key: '60', label: '60 Gün', value: marsPrices.price_60 || '0' },
                        { key: '90', label: '90 Gün', value: marsPrices.price_90 || '0' },
                        { key: '120', label: '120 Gün', value: marsPrices.price_120 || '0' },
                        { key: '150', label: '150 Gün', value: marsPrices.price_150 || '0' },
                        { key: '180', label: '180 Gün', value: marsPrices.price_180 || '0' },
                      ].map((tier) => {
                        const isActive = profitSettings.enabled && profitSettings.marsPriceTier === tier.key;
                        return (
                          <div key={tier.key} className="flex justify-between items-center px-3 py-1.5">
                            <span className="text-sm text-muted-foreground">
                              {tier.label}
                              {isActive ? ' (Seçili)' : ''}
                            </span>
                            <span className={`text-sm font-semibold ${isActive ? 'text-blue-700 dark:text-blue-300' : ''}`}>
                              {formatPrice(parseFloat(tier.value))}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              
              <Separator />
              
              {product.price > 0 && product.current_cost > 0 && (
                <div className="rounded-lg border divide-y">
                  <div className="flex justify-between items-center px-3 py-1.5">
                    <span className="text-sm text-muted-foreground">Kar Marjı</span>
                    <span className="text-sm font-bold text-green-600">
                      %{(((product.price - product.current_cost) / product.price) * 100).toFixed(1)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center px-3 py-1.5">
                    <span className="text-sm text-muted-foreground">Birim Kar</span>
                    <span className="text-sm font-bold text-green-600">
                      {formatPrice(product.price - product.current_cost)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center px-3 py-1.5">
                    <span className="text-sm text-muted-foreground">Toplam Stok Değeri</span>
                    <span className="text-sm font-bold text-accent">
                      {formatPrice((product.current_cost || 0) * product.quantity_on_hand)}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action Bar — auxiliary tools as dialog-triggered buttons */}
        <div className="flex flex-wrap gap-2">
          <Dialog open={isCalcOpen} onOpenChange={setIsCalcOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Calculator className="w-4 h-4" />
                Fiyat Hesaplayıcı
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Fiyat Hesaplayıcı
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="sale-price">Satış Fiyatı (₺)</Label>
                  <Input
                    id="sale-price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    placeholder="Satış fiyatı girin"
                  />
                </div>
                <div>
                  <Label htmlFor="discount">İndirim (₺)</Label>
                  <Input
                    id="discount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    placeholder="İndirim tutarı"
                  />
                </div>
                <div>
                  <Label htmlFor="quantity">Adet</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  />
                </div>
                {calculateProfit && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <div className="text-sm text-muted-foreground mb-1">İndirimli Fiyat (Birim)</div>
                        <div className="text-lg font-bold text-accent">
                          {formatPrice(calculateProfit.priceAfterDiscount)}
                        </div>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <div className="text-sm text-muted-foreground mb-1">Birim Kar</div>
                        <div className="text-lg font-bold text-green-600">
                          {formatPrice(calculateProfit.profitPerUnit)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Kar Marjı: %{calculateProfit.profitMargin.toFixed(1)}
                        </div>
                      </div>
                      <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        <div className="text-sm text-muted-foreground mb-1">Toplam Gelir</div>
                        <div className="text-lg font-bold text-blue-600">
                          {formatPrice(calculateProfit.totalRevenue)}
                        </div>
                      </div>
                      <div className="p-3 bg-green-600/10 rounded-lg border border-green-600/20">
                        <div className="text-sm text-muted-foreground mb-1">Toplam Kar</div>
                        <div className="text-xl font-bold text-green-700">
                          {formatPrice(calculateProfit.totalProfit)}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isSpecialPriceOpen} onOpenChange={setIsSpecialPriceOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <UserCheck className="w-4 h-4" />
                Özel Fiyat Talebi
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Özel Fiyat Talebi
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="customer-select">Müşteri Seç</Label>
                    <div className="flex gap-2">
                      <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Müşteri seçin..." />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map(customer => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {getCustomerFullName(customer)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setShowAddCustomerModal(true)}
                        title="Yeni Müşteri Ekle"
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="requested-price">Talep Edilen Fiyat (₺)</Label>
                    <Input
                      id="requested-price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={requestedPrice}
                      onChange={(e) => setRequestedPrice(e.target.value)}
                      placeholder="Birim fiyat girin"
                    />
                  </div>
                  <div>
                    <Label htmlFor="special-quantity">Adet</Label>
                    <Input
                      id="special-quantity"
                      type="number"
                      min="1"
                      value={specialPriceQuantity}
                      onChange={(e) => setSpecialPriceQuantity(parseInt(e.target.value) || 1)}
                      placeholder="Adet"
                    />
                  </div>
                </div>
                {requestedPrice && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-accent/10 rounded-lg border border-accent/20">
                      <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">Toplam Tutar</div>
                        <div className="text-lg font-bold text-accent">
                          {formatPrice((parseFloat(requestedPrice) || 0) * specialPriceQuantity)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {specialPriceQuantity} adet × {formatPrice(parseFloat(requestedPrice) || 0)}
                        </div>
                      </div>
                    </div>
                    {product.current_cost > 0 && (
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <div className="text-center">
                          <div className="text-sm text-muted-foreground mb-1">Toplam Kar</div>
                          <div className="text-lg font-bold text-green-600">
                            {formatPrice(((parseFloat(requestedPrice) || 0) - product.current_cost) * specialPriceQuantity)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Birim kar: {formatPrice((parseFloat(requestedPrice) || 0) - product.current_cost)}
                          </div>
                          {parseFloat(requestedPrice) > 0 && (
                            <div className="text-xs font-semibold text-green-700 mt-1">
                              Kar Marjı: %{(((parseFloat(requestedPrice) - product.current_cost) / parseFloat(requestedPrice)) * 100).toFixed(1)}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label htmlFor="special-note">Not</Label>
                  <Textarea
                    id="special-note"
                    value={specialPriceNote}
                    onChange={(e) => setSpecialPriceNote(e.target.value)}
                    placeholder="Özel fiyat gerekçesi..."
                    rows={3}
                  />
                </div>
                <Button
                  onClick={() => {
                    handleSpecialPriceRequest();
                    setIsSpecialPriceOpen(false);
                  }}
                  disabled={!selectedCustomer || !requestedPrice}
                  className="gap-2"
                >
                  <UserCheck className="w-4 h-4" />
                  Özel Fiyat Talep Et ({formatPrice((parseFloat(requestedPrice) || 0) * specialPriceQuantity)})
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShowNewVisitModal(true)}
          >
            <Users className="w-4 h-4" />
            Ziyaret İşlemleri
          </Button>

          <Dialog open={isPackageOpen} onOpenChange={setIsPackageOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <ShoppingBag className="w-4 h-4" />
                Pakete Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5" />
                  Pakete Ekle
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="package-select">Paket Seç</Label>
                  <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Mevcut paket seç veya yeni oluştur..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="create-new">
                        <div className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Yeni Paket Oluştur
                        </div>
                      </SelectItem>
                      {openPackages.map(pkg => (
                        <SelectItem key={pkg.id} value={pkg.id}>
                          {getPackageDisplayName(pkg)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="package-qty">Adet</Label>
                  <Input
                    id="package-qty"
                    type="number"
                    min="1"
                    value={packageQuantity}
                    onChange={(e) => setPackageQuantity(parseInt(e.target.value) || 1)}
                  />
                </div>
                {selectedPackage && selectedPackage !== 'create-new' && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground">Seçilen Paket:</div>
                    <div className="font-medium">{getPackageDisplayName(openPackages.find(p => p.id === selectedPackage)!)}</div>
                  </div>
                )}
                <Button
                  onClick={async () => {
                    await handleAddToPackage();
                    setIsPackageOpen(false);
                  }}
                  className="w-full gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {selectedPackage && selectedPackage !== 'create-new' ? 'Pakete Ekle' : 'Yeni Paket Oluştur'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* On Hold Records */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Beklemedeki Kayıtlar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {onHoldRecords.length > 0 ? (
              <div className="rounded-lg border divide-y">
                {onHoldRecords.map((record) => {
                  const customer = record.customer;
                  const customerName = customer 
                    ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bilinmeyen Müşteri'
                    : 'Müşterisiz';
                  const daysOnHold = Math.floor((new Date().getTime() - new Date(record.date_on_hold).getTime()) / (1000 * 60 * 60 * 24));
                  
                  return (
                    <div key={record.id} className="px-3 py-1.5">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        {customer?.id ? (
                          <Link
                            to={`/customers/${customer.id}`}
                            className="text-sm font-semibold text-primary hover:underline underline-offset-4"
                          >
                            {customerName}
                          </Link>
                        ) : (
                          <div className="text-sm font-semibold">{customerName}</div>
                        )}
                        {customer?.phone && (
                          <div className="text-xs text-muted-foreground">{customer.phone}</div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {new Date(record.date_on_hold).toLocaleDateString('tr-TR')}
                        </div>
                        <span className="text-muted-foreground">•</span>
                        <span><span className="text-muted-foreground">Adet:</span> <span className="font-medium">{record.hold_quantity}</span></span>
                        <span><span className="text-muted-foreground">Fiyat:</span> <span className="font-medium">{formatPrice(record.hold_price)}</span></span>
                        <span><span className="text-muted-foreground">Toplam:</span> <span className="font-medium">{formatPrice(record.hold_price * record.hold_quantity)}</span></span>
                        <Badge variant="outline" className="text-xs ml-auto shrink-0">
                          {daysOnHold} gün
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                Bu ürün için beklemede kayıt bulunmamaktadır.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Last Sales */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Son Satışlar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lastSales.length > 0 ? (
              <div className="rounded-lg border divide-y">
                {lastSales.map((saleItem) => {
                  const sale = saleItem.sale;
                  const customer = sale?.customer;
                  const customerName = customer 
                    ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Bilinmeyen Müşteri'
                    : 'Müşterisiz';
                  
                  return (
                    <div key={saleItem.id} className="px-3 py-1.5">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        {customer?.id ? (
                          <Link
                            to={`/customers/${customer.id}`}
                            className="text-sm font-semibold text-primary hover:underline underline-offset-4"
                          >
                            {customerName}
                          </Link>
                        ) : (
                          <div className="text-sm font-semibold">{customerName}</div>
                        )}
                        {sale?.sale_datetime && (
                          <div className="text-xs text-muted-foreground">
                            {new Date(sale.sale_datetime).toLocaleString('tr-TR')}
                          </div>
                        )}
                        <span className="text-muted-foreground">•</span>
                        <span><span className="text-muted-foreground">Adet:</span> <span className="font-medium">{saleItem.quantity}</span></span>
                        <span><span className="text-muted-foreground">Birim Fiyat:</span> <span className="font-medium">{formatPrice(saleItem.sale_price_each || 0)}</span></span>
                        {saleItem.discount > 0 && (
                          <span><span className="text-muted-foreground">İndirim:</span> <span className="font-medium text-red-600">-{formatPrice(saleItem.discount)}</span></span>
                        )}
                        {saleItem.cost_each > 0 && (
                          <span>
                            <span className="text-muted-foreground">Kar:</span>{' '}
                            <span className="font-medium text-green-600">
                              {formatPrice((saleItem.sale_price_each || 0) - saleItem.discount - saleItem.cost_each)}
                            </span>
                          </span>
                        )}
                        <Badge variant="secondary" className="ml-auto shrink-0">
                          {formatPrice(saleItem.line_total_net)}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                Bu ürün için henüz satış kaydı bulunmamaktadır.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Product Movements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Stok Hareketleri
            </CardTitle>
            <CardDescription>
              Önceki/Sonraki stok değerleri tüm hareket geçmişi üzerinden hesaplanır. Listede tüm hareketler gösterilir.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {movementsWithBalance.length > 0 && product ? (
              <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Güncel stok:</span>
                <span className="font-semibold tabular-nums">{product.quantity_on_hand ?? 0} adet</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">
                  Toplam {movementsWithBalance.length} hareket gösteriliyor
                </span>
              </div>
            ) : null}
            {displayedMovementsWithBalance.length > 0 ? (
              <div className="space-y-3">
                {displayedMovementsWithBalance.map((row) => {
                  const movement = row.movement;
                  const actor = movement.actor;
                  const actorName = actor?.full_name || actor?.email?.split('@')[0] || 'Bilinmeyen kullanıcı';
                  const isIn = row.signedDelta >= 0;
                  const extras = movementExtras[movement.id];
                  const reasonHref = extras?.href;
                  const customerId = extras?.customerId;
                  const customerName = extras?.customerName;
                  const sourceLabel = movement.related_table === 'manual_entry'
                    ? 'Manuel giriş'
                    : movement.related_table === 'manual_adjustment'
                      ? 'Manuel düzeltme'
                      : movement.related_table === 'sales'
                        ? 'Satış'
                        : movement.related_table === 'trades'
                          ? 'Bayi ticareti'
                          : movement.related_table === 'eirsaliye_item_import'
                            ? 'e-İrsaliye'
                            : movement.related_table || '';

                  return (
                    <div
                      key={movement.id}
                      className={`flex flex-col gap-1 p-3 rounded-md border-l-4 ${
                        isIn
                          ? 'border-l-green-500 bg-green-50/40 dark:bg-green-950/10'
                          : 'border-l-red-500 bg-red-50/40 dark:bg-red-950/10'
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                        {isIn ? (
                          <ArrowUp className="w-4 h-4 text-green-600 shrink-0" />
                        ) : (
                          <ArrowDown className="w-4 h-4 text-red-600 shrink-0" />
                        )}
                        <span
                          className={`font-bold tabular-nums ${
                            isIn ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                          }`}
                        >
                          {row.signedDelta >= 0 ? '+' : ''}
                          {row.signedDelta} adet
                        </span>
                        <span className="text-muted-foreground tabular-nums">
                          {row.stockBefore} → <span className="font-medium text-foreground">{row.stockAfter}</span>
                        </span>
                        <Badge variant="secondary" className={getMovementTypeColor(movement.movement_type)}>
                          {sourceLabel || getMovementTypeLabel(movement.movement_type)}
                        </Badge>
                        <span className="text-xs text-muted-foreground tabular-nums ml-auto">
                          {new Date(movement.created_at).toLocaleString('tr-TR')}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pl-7">
                        <span>
                          <span className="font-medium text-foreground">İşlemi yapan:</span> {actorName}
                        </span>
                        {customerId && customerName ? (
                          <span>
                            <span className="font-medium text-foreground">Müşteri:</span>{' '}
                            <Link
                              to={`/customers/${customerId}`}
                              className="text-primary underline-offset-4 hover:underline"
                            >
                              {customerName}
                            </Link>
                          </span>
                        ) : null}
                        {reasonHref ? (
                          <Link
                            to={reasonHref}
                            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline ml-auto"
                          >
                            Detay
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                Bu ürün için henüz stok hareketi kaydı bulunmamaktadır.
              </p>
            )}
          </CardContent>
        </Card>

        {/* New Visit Modal */}
        <NewVisitModal
          open={showNewVisitModal}
          onOpenChange={setShowNewVisitModal}
          preselectedProduct={product ? {
            id: product.id,
            name: product.name,
            price: product.price,
            category: product.category,
            item_code: product.item_code
          } : undefined}
        />
        
        {/* Add Customer Modal */}
        <AddCustomerModal
          isOpen={showAddCustomerModal}
          onClose={() => setShowAddCustomerModal(false)}
          onCustomerAdded={handleCustomerAdded}
        />
      </div>
    </Layout>
  );
};

export default ProductDetail;